import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import Product3DCard from "./Product3DCard";

// Import product images
import bolPersonnalise from "@/assets/products/bol-personnalise.png";
import sacPersonnalise from "@/assets/products/sac-personnalise-ams-duo.png";
import sacKraftPoignees from "@/assets/products/sac-kraft-poignees.png";
import bolKraftSalade from "@/assets/products/bol-kraft-salade-couvercle.jpg";

const ProductsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const navigate = useNavigate();

  // Mapping des catégories vers leurs pages dédiées
  const categoryLinks: { [key: string]: string } = {
    "Gobelets": "/produits/gobelets-kraft",
    "Pailles papier": "/produits/pailles",
    "Barquettes": "/produits/barquettes-bateau",
    "Pots à soupe": "/produits/pots-soupe",
    "Sachets alimentaires": "/produits",
    "Film étirable": "/produits",
    "Serviettes": "/produits",
    "Bobines & essuie-mains": "/produits",
    "Gants": "/produits",
    
  };

  // Fonction pour naviguer vers la page du produit
  const handleCategoryClick = (category: string) => {
    const link = categoryLinks[category] || "/produits";
    navigate(link);
  };

  const products = [
    {
      id: 1,
      name: "Bols personnalisés",
      description: "Bols kraft avec votre logo pour une identité de marque forte",
      image: bolPersonnalise,
      tags: ["FSC", "Personnalisable"],
    },
    {
      id: 2,
      name: "Sacs personnalisés",
      description: "Sacs kraft avec impression de votre logo pour promouvoir votre établissement",
      image: sacPersonnalise,
      tags: ["FSC", "Personnalisable"],
    },
    {
      id: 3,
      name: "Sacs kraft",
      description: "Sacs en papier kraft recyclable pour la vente à emporter",
      image: sacKraftPoignees,
      tags: ["FSC", "Recyclable"],
    },
    {
      id: 4,
      name: "Bols kraft",
      description: "Bols écologiques pour salades, soupes et plats chauds",
      image: bolKraftSalade,
      tags: ["Compostable", "Contact alimentaire"],
    },
  ];

  return (
    <section className="py-28 bg-gradient-to-b from-secondary via-secondary/80 to-background relative overflow-hidden" ref={ref}>
      {/* Bold Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="absolute top-20 left-10 w-96 h-96 rounded-full bg-primary/15 blur-[120px]" />
        <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-eco/15 blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-primary/10 rounded-full" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-block text-sm font-bold text-primary uppercase tracking-widest mb-4 bg-primary/10 px-4 py-2 rounded-full"
            >
              Nos solutions
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display text-4xl md:text-5xl font-bold text-foreground"
            >
              Nos Gammes <span className="text-primary">Principales</span>
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link to="/produits">
              <Button variant="default" size="lg" className="mt-6 md:mt-0 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30">
                Voir tous les produits
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, index) => (
            <Product3DCard key={product.id} product={product} index={index} />
          ))}
        </div>

        {/* More Categories */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-muted-foreground mb-6 font-medium">Également disponibles :</p>
          <div className="flex flex-wrap justify-center gap-3">
            {["Gobelets", "Pailles papier", "Barquettes", "Pots à soupe", "Sachets alimentaires", "Film étirable", "Serviettes", "Bobines & essuie-mains", "Gants"].map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryClick(cat)}
                className="px-5 py-2.5 bg-card border-2 border-border rounded-full text-sm font-medium text-foreground hover:border-primary hover:bg-primary/5 hover:text-primary transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
              >
                {cat}
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsSection;